import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { BlockHourPeriod, Project, TicketDetail } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from './Card.tsx';

interface Props {
  ticket: TicketDetail;
  totalMinutes: number;
}

interface ProjectResponse {
  project: Project;
}

interface PeriodResponse {
  period: BlockHourPeriod | null;
}

function fmtH(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}h`;
}

function readQuotedHours(custom: Record<string, unknown>): number | null {
  const raw = custom['quoted_hours'];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function TicketHoursCard({ ticket, totalMinutes }: Props): React.ReactElement | null {
  const [project, setProject] = useState<Project | null>(null);
  const [period, setPeriod] = useState<BlockHourPeriod | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Project takes precedence: any ticket attached to a project shows the
  // project budget, regardless of its type. Otherwise we look at type to
  // decide whether to fetch the org's current block-hour period.
  const needsProject = Boolean(ticket.projectId);
  const needsPeriod = !needsProject && (ticket.type === 'escalation' || ticket.type === 'chat');

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    setPeriod(null);
    setReady(false);
    setLoadError(null);

    async function go() {
      try {
        if (needsProject && ticket.projectId) {
          const r = await apiFetch<ProjectResponse>(`/api/projects/${ticket.projectId}`);
          if (!cancelled) setProject(r.project);
        } else if (needsPeriod) {
          const r = await apiFetch<PeriodResponse>(
            `/api/block-hours/${ticket.organizationId}/periods/current`,
          );
          if (!cancelled) setPeriod(r.period);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Failed to load hours.');
      } finally {
        if (!cancelled) setReady(true);
      }
    }
    void go();
    return () => {
      cancelled = true;
    };
  }, [needsProject, needsPeriod, ticket.projectId, ticket.organizationId]);

  const thisTicketHours = totalMinutes / 60;
  const quotedHours = readQuotedHours(ticket.customFields ?? {});

  // ─── Project-attached ticket ────────────────────────────────────────────
  if (needsProject) {
    if (!ready) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
        </Card>
      );
    }
    if (loadError || !project) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-danger)]">
            {loadError ?? 'Project not found.'}
          </p>
          <p className="mt-1 text-sm text-[var(--color-body)]">
            <strong>{fmtH(thisTicketHours)}</strong> logged on this ticket.
          </p>
        </Card>
      );
    }
    const budget = project.budgetedHours;
    const projectUsed = project.consumedMinutes / 60;
    const remaining = Math.max(0, budget - projectUsed);
    const pct = budget > 0 ? Math.min(100, (projectUsed / budget) * 100) : 0;
    const over = budget > 0 && projectUsed >= budget * 0.8;
    return (
      <Card>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <Link
            to={`/projects/${project.id}`}
            className="truncate text-xs text-[var(--color-t3-blue)] hover:underline"
            title={project.name}
          >
            {project.name}
          </Link>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Stat label="Project budget" value={fmtH(budget)} />
          <Stat
            label="Project used"
            value={fmtH(projectUsed)}
            sub={budget > 0 ? `${pct.toFixed(0)}%` : undefined}
            danger={over}
          />
          <Stat label="This ticket" value={fmtH(thisTicketHours)} />
          <Stat label="Remaining" value={fmtH(remaining)} danger={over} />
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
          <div
            className={'h-full ' + (over ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]')}
            style={{ width: `${pct}%` }}
          />
        </div>
      </Card>
    );
  }

  // ─── Quote ticket (no linked project yet) ───────────────────────────────
  if (ticket.type === 'quote') {
    if (quotedHours === null) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-muted)]">
            No quoted hours set. Add <strong>Quoted hours</strong> in the custom fields panel.
          </p>
          <p className="mt-2 text-sm text-[var(--color-body)]">
            <strong>{fmtH(thisTicketHours)}</strong> logged on this ticket.
          </p>
        </Card>
      );
    }
    const pct = quotedHours > 0 ? Math.min(100, (thisTicketHours / quotedHours) * 100) : 0;
    const over = quotedHours > 0 && thisTicketHours >= quotedHours * 0.8;
    return (
      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Quote budget for the proposed work.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Stat label="Quoted" value={fmtH(quotedHours)} />
          <Stat
            label="Logged"
            value={fmtH(thisTicketHours)}
            sub={quotedHours > 0 ? `${pct.toFixed(0)}%` : undefined}
            danger={over}
          />
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
          <div
            className={'h-full ' + (over ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]')}
            style={{ width: `${pct}%` }}
          />
        </div>
      </Card>
    );
  }

  // ─── Escalation / Chat with no project — show org's block-hour pool ────
  if (needsPeriod) {
    if (!ready) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
        </Card>
      );
    }
    if (loadError) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-danger)]">{loadError}</p>
        </Card>
      );
    }
    if (!period) {
      return (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <p className="mt-3 text-sm text-[var(--color-muted)]">
            {ticket.organizationName} has no active block-hour subscription.
          </p>
          <p className="mt-2 text-sm text-[var(--color-body)]">
            <strong>{fmtH(thisTicketHours)}</strong> logged on this ticket.
          </p>
        </Card>
      );
    }
    const granted = period.totalAllocation;
    const used = period.consumedHours;
    const remaining = period.remainingHours;
    const pct = granted > 0 ? Math.min(100, (used / granted) * 100) : 0;
    const over = granted > 0 && used >= granted * 0.8;
    return (
      <Card>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
          <span className="text-xs text-[var(--color-muted)]">Block hours · this period</span>
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Escalations and chats draw from {ticket.organizationName}'s monthly pool.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Stat label="Granted" value={fmtH(granted)} />
          <Stat
            label="Used"
            value={fmtH(used)}
            sub={granted > 0 ? `${pct.toFixed(0)}%` : undefined}
            danger={over}
          />
          <Stat label="This ticket" value={fmtH(thisTicketHours)} />
          <Stat label="Remaining" value={fmtH(remaining)} danger={remaining < 0 || over} />
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
          <div
            className={'h-full ' + (over ? 'bg-[var(--color-danger)]' : 'bg-[var(--color-t3-blue)]')}
            style={{ width: `${pct}%` }}
          />
        </div>
      </Card>
    );
  }

  // Project ticket without a linked project (defensive fallback), or any
  // other type we haven't categorized — just show the simple count.
  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">Hours</h2>
      <p className="mt-2 text-sm text-[var(--color-body)]">
        <strong>{fmtH(thisTicketHours)}</strong> logged on this ticket.
      </p>
    </Card>
  );
}

function Stat({
  label,
  value,
  sub,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  danger?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </div>
      <div
        className={
          'mt-0.5 font-mono text-base font-bold ' +
          (danger ? 'text-[var(--color-danger)]' : 'text-[var(--color-header)]')
        }
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[10px] text-[var(--color-muted)]">{sub}</div>}
    </div>
  );
}
